import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button, Input, useDisclosure } from "@nextui-org/react";
import { SearchIcon } from "../../../assets/icons/SearchIcon";
import { PlusIcon } from "../../../assets/icons/PlusIcon";
import { useFolderContentStore } from "../../../components/home_page/cards/FolderCard";
import { useUserResources } from "src/hooks/useUserResourcesHooks";
import { useAlertStore } from "../../../components/common/CustomAlert";
import DraggingArea from "../../../components/common/DraggingArea";
import FileCard from "../../../components/home_page/cards/FileCard";
import FolderCard from "../../../components/home_page/cards/FolderCard";
import AddNewFolderModal from "../../../components/modals/AddNewFolderModal";
import { Item, Type } from "src/types/api/ListFolderFiles";
import PaginationWrapper from "../../../components/common/PaginationWrapper";

// Dummy data for club support program
const dummyClubSupportFiles: Item[] = [
  {
    file_id: "1",
    name: "Club_Registration_Form.pdf",
    type: Type.File,
    file_size: 245760,
    created_at: "2024-01-15T10:30:00Z",
    updated_at: "2024-01-15T10:30:00Z",
    is_favorite: 0,
    mime_type: "application/pdf",
    file_path: "/club_support/Club_Registration_Form.pdf",
    file_type: "pdf",
    original_filename: "Club_Registration_Form.pdf"
  },
  {
    file_id: "2",
    name: "Support_Program_Guidelines.docx",
    type: Type.File,
    file_size: 189440,
    created_at: "2024-01-14T14:20:00Z",
    updated_at: "2024-01-14T14:20:00Z",
    is_favorite: 1,
    mime_type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    file_path: "/club_support/Support_Program_Guidelines.docx",
    file_type: "docx",
    original_filename: "Support_Program_Guidelines.docx"
  },
  {
    file_id: "3",
    name: "Club_Activities_2024.xlsx",
    type: Type.File,
    file_size: 156672,
    created_at: "2024-01-13T09:15:00Z",
    updated_at: "2024-01-13T09:15:00Z",
    is_favorite: 0,
    mime_type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    file_path: "/club_support/Club_Activities_2024.xlsx",
    file_type: "xlsx",
    original_filename: "Club_Activities_2024.xlsx"
  },
  {
    file_id: "4",
    name: "Member_Directory.csv",
    type: Type.File,
    file_size: 32768,
    created_at: "2024-01-12T16:45:00Z",
    updated_at: "2024-01-12T16:45:00Z",
    is_favorite: 0,
    mime_type: "text/csv",
    file_path: "/club_support/Member_Directory.csv",
    file_type: "csv",
    original_filename: "Member_Directory.csv"
  },
  {
    file_id: "5",
    name: "Club_Events_Presentation.pptx",
    type: Type.File,
    file_size: 5242880,
    created_at: "2024-01-11T11:00:00Z",
    updated_at: "2024-01-11T11:00:00Z",
    is_favorite: 1,
    mime_type: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    file_path: "/club_support/Club_Events_Presentation.pptx",
    file_type: "pptx",
    original_filename: "Club_Events_Presentation.pptx"
  }
];

const dummyClubSupportFolders: Item[] = [
  {
    folder_id: "6",
    name: "Club_Meetings",
    type: Type.Folder,
    file_size: 0,
    created_at: "2024-01-10T08:00:00Z",
    updated_at: "2024-01-10T08:00:00Z",
    is_favorite: 0,
    mime_type: "folder",
    file_path: "/club_support/Club_Meetings"
  },
  {
    folder_id: "7",
    name: "Training_Materials",
    type: Type.Folder,
    file_size: 0,
    created_at: "2024-01-09T13:30:00Z",
    updated_at: "2024-01-09T13:30:00Z",
    is_favorite: 1,
    mime_type: "folder",
    file_path: "/club_support/Training_Materials"
  },
  {
    folder_id: "8",
    name: "Financial_Reports",
    type: Type.Folder,
    file_size: 0,
    created_at: "2024-01-08T10:15:00Z",
    updated_at: "2024-01-08T10:15:00Z",
    is_favorite: 0,
    mime_type: "folder",
    file_path: "/club_support/Financial_Reports"
  },
  {
    folder_id: "9",
    name: "Event_Photos",
    type: Type.Folder,
    file_size: 0,
    created_at: "2024-01-07T15:20:00Z",
    updated_at: "2024-01-07T15:20:00Z",
    is_favorite: 0,
    mime_type: "folder",
    file_path: "/club_support/Event_Photos"
  }
];

const ClubSupportProgramFiles: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { setFolderId } = useFolderContentStore();
  const { userResources } = useUserResources();
  const { showAlert } = useAlertStore();

  // State for search and pagination
  const [searchTerm, setSearchTerm] = React.useState("");
  const [currentPage, setCurrentPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(12);

  const {
    isOpen: isAddNewOpen,
    onOpen: onOpenAddNew,
    onClose: onCloseAddNew,
  } = useDisclosure();

  // Filter items based on search term
  const filteredItems = useMemo(() => {
    const allItems = [...dummyClubSupportFolders, ...dummyClubSupportFiles];
    if (!searchTerm) return allItems;
    
    return allItems.filter(item =>
      item.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm]);

  // Paginate items
  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredItems.slice(startIndex, endIndex);
  }, [filteredItems, currentPage, rowsPerPage]);

  // Handle search change
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  }, []);

  // Handle page change
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  // Handle drop (drag and drop functionality)
  const handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    showAlert("Dropped files", "success");
  }, [showAlert]);



  const topContent = useMemo(
    () => (
      <div className="flex flex-col gap-4">
        <div className="flex justify-between gap-3 items-end">
          <Input
            isClearable
            classNames={{
              base: "lg:w-full sm:max-w-[44%]",
              inputWrapper: "border-1 bg-white",
            }}
            placeholder={t("SearchByName")}
            startContent={<SearchIcon className="text-default-300" />}
            value={searchTerm}
            variant="bordered"
            onClear={() => setSearchTerm("")}
            onChange={handleSearchChange}
          />
          <div className="flex gap-3">
            <Button
              className="ml-3 text-white"
              color="success"
              endContent={<PlusIcon />}
              size="sm"
              onClick={onOpenAddNew}
            >
              {t("AddNew")}
            </Button>
          </div>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-default-400 text-small">
            {t("TotalFiles")}: {filteredItems.length}
          </span>
        </div>
      </div>
    ),
    [t, searchTerm, filteredItems.length, onOpenAddNew, handleSearchChange]
  );



  return (
    <DraggingArea
      key={`${i18n.language}`} // Force re-render when language changes
      onDrop={handleDrop}
      canUpload={userResources?.can_upload ?? true}
    >
      {topContent}
      
      <PaginationWrapper
        currentPage={currentPage}
        totalItems={filteredItems.length}
        itemsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
      >
        <div className="flex flex-wrap gap-6 justify-start items-center mt-4">
          {paginatedItems.map((item) => {
            return item.type === Type.Folder ? (
              <FolderCard key={item.folder_id} folder={item} />
            ) : (
              <FileCard key={item.file_id} file={item} />
            );
          })}
        </div>
      </PaginationWrapper>
      
      <AddNewFolderModal isOpen={isAddNewOpen} onClose={onCloseAddNew} />
    </DraggingArea>
  );
};

export default ClubSupportProgramFiles;
